import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/cn";

// ════════════════════════════════════════════════════════════════════════
// TimecodeChip — mono-font label for timecodes, frame markers, video meta.
// Always on a dark ink surface so it reads as broadcast UI.
// ════════════════════════════════════════════════════════════════════════

export const timecodeChipVariants = cva(
  [
    "inline-flex items-center gap-1.5 rounded-sm",
    "font-mono text-[11px] font-semibold tracking-mono uppercase",
    "px-2 py-1 whitespace-nowrap leading-none",
  ].join(" "),
  {
    variants: {
      variant: {
        // Standard — inverse ink pair. Club accent variants are
        // surface-oriented and must not become inverse text.
        default: "bg-ink text-ink-inverse",
        // Flag — review needed.
        flag: "bg-ink text-flag",
        // Past — already reviewed.
        past: "bg-ink/70 text-ink-inverse",
        // Subtle — for inline use over light surfaces.
        ghost: "border border-rule-strong text-ink bg-transparent",
      },
    },
    defaultVariants: { variant: "default" },
  },
);

export interface TimecodeChipProps
  extends React.HTMLAttributes<HTMLSpanElement>,
    VariantProps<typeof timecodeChipVariants> {}

export const TimecodeChip = React.forwardRef<
  HTMLSpanElement,
  TimecodeChipProps
>(({ className, variant, ...props }, ref) => (
  <span
    ref={ref}
    className={cn(timecodeChipVariants({ variant, className }))}
    {...props}
  />
));
TimecodeChip.displayName = "TimecodeChip";
